// src/modals/ChecklistRunModal.jsx — Phase 4 cascade PR #133/136 (ADR-076)
// + PR #141 (ADR-077) : fetch infos logement via /api/admin-users (RLS
// storage tightened bloque la lecture client direct de fbz8-cfg).
//
// Modale full-screen mobile : parcours checklist d'un menage_run.
// Ouverte depuis MenageTodayView au click "📋 Voir checklist".
// - Liste pièces depuis info.menage.pieces (endpoint dédié)
// - Items checked → debounce save items_checked JSONB
// - Photos via menage-photo-queue (capture caméra) + badges status upload
// - Footer sticky : récap pièces complètes + bouton "✅ Terminer"
//   disabled si validation pas OK (photos_min par pièce)
// - LOT C (contrat v2 art. 7.2) : pour l'AGENT (menage/orchestrateur), au moins
//   1 photo de pièce (uploadée OU en file locale) est OBLIGATOIRE pour terminer
//   — les photos horodatées de fin de ménage FONT FOI de l'état du logement.
//   La clôture agent passe par l'action serveur `menage_run_complete` (garde
//   photo fail-closed + écriture user-scoped : la RLS 48h reste l'arbitre).
//   ADMIN exempté (chemin direct historique conservé — correction a posteriori).
// - Si checklist non configurée admin → rien à valider côté items, mais l'agent
//   dispose d'un bloc photo générique (la photo de fin de ménage reste requise)
// - AccessSection en haut du body (adresse + codes structurés, utile
//   pendant le ménage sur place — header label retiré PR #148)

import { useState, useEffect, useMemo, useRef } from "react"
import FeuilleConfirmation from '../components/FeuilleConfirmation'
import { Lock, Calendar, Clock, Check, AlertCircle, AlertTriangle, Info, RefreshCw, Camera, Image as ImageIcon, Loader2 } from "lucide-react"
import { supabase } from "../supabase"
import { DS } from "../lib/ui"
import { enqueuePhoto, getQueueForRun } from "../lib/menage-photo-queue"
import { fetchLogInfo, invalidateLogInfo } from "../lib/menage-log-info-cache"
import { groupPhotosByPiece } from "../lib/menage-run-restitution"
import { AccessSection, TimingSection, todayParisIso } from "../views/MenageTodayView"
import { isMenageRunLockedForMenage, isRunOpenForMenageToday } from "../lib/menage-status-helpers"
import { formatDateFR } from "../lib/format"
import useEchapPourFermer from "../lib/useEchapPourFermer"
import SectionSignalerManque from "../views/menage/SectionSignalerManque"
import BlocAApporter from "../views/menage/BlocAApporter"
import { fetchMyInstructions } from "../lib/menage-instructions-api"           // ADR-302 — consignes internes

// LOT C — pièce SYNTHÉTIQUE pour le bloc photo générique (logement sans
// checklist configurée) : permet à l'agent de satisfaire l'obligation de photo
// de fin de ménage (contrat v2 art. 7.2) même sans pièces admin. Path Storage :
// {logement_id}/runs/{run_id}/general-{photo_id}.jpg (couvert par les policies
// menage_photos_* — préfixe logement_id inchangé).
const GENERIC_PHOTO_PIECE = { id: 'general', name: 'Photo de fin de ménage', icon: '📷', items: [], photos_min: 1 }

// ⚠️ DOUBLE-FORME d'items_checked (GO Dimitri 2026-08-19) : la forme legacy
// est BOOLÉENNE par pièce (sols: true = pièce entière cochée) — la modale
// supposait un tableau et CRASHAIT (`true.includes`) : écran d'erreur en
// plein ménage, l'agent bloqué. Tolérance ici, au point de consommation :
// true → tous les items de la pièce cochés ; false/illisible → aucun.
// Le premier toggle réécrit la pièce en forme tableau (la forme moderne).
// Réglages (modifiables par les tests) : durée maximale d'attente des
// sauvegardes de coches avant « Terminer » (lot « travail perdu »).
export const _reglagesChecklist = { attenteMaxMs: 20000 }

export function cochesEnListe(valeur, piece) {
  if (Array.isArray(valeur)) return valeur
  if (valeur === true) return (piece?.items || []).filter(Boolean).map((it) => it.id)
  return []
}

export default function ChecklistRunModal({ run, currentUser, cfg, onClose, onCompleted, onIncident }) {
  // Échap ferme — consigne 2026-08-14, hook partagé (pile anti-double-fermeture).
  useEchapPourFermer(onClose)

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [confirmFin, setConfirmFin] = useState(false)
  // Erreur d'ajout de photo — DANS l'écran (plus d'alerte du navigateur).
  const [erreurPhoto, setErreurPhoto] = useState(null)
  const [itemsChecked, setItemsChecked] = useState(run.items_checked || {})
  const [idbCount, setIdbCount] = useState({})
  const [photos, setPhotos] = useState(run.photos || [])
  const debounceTimer = useRef(null)
  const pendingItems = useRef(null)   // coches pas encore envoyées
  const saveInFlight = useRef(null)   // sauvegarde en cours (promesse)
  const startedRef = useRef(false)    // passage « en cours » déjà fait
  const flushRef = useRef(null)       // dernière version de flushItems
  // Fermeture de la fenêtre : la minuterie de sauvegarde est ANNULÉE (elle ne
  // part jamais après coup) et une coche encore en attente est envoyée TOUT
  // DE SUITE plutôt que perdue. flushItems gère et affiche ses erreurs.
  useEffect(() => () => {
    if (!debounceTimer.current && !pendingItems.current) return
    if (debounceTimer.current) { clearTimeout(debounceTimer.current); debounceTimer.current = null }
    if (pendingItems.current && flushRef.current) {
      flushRef.current().catch((e) => console.error('[checklist] coche non envoyée à la fermeture', e?.message || e))
    }
  }, [])

  // ADR-230 — verrouillage anti-anticipation simplifié :
  //   - run pas du jour (futur ou veille hors fenêtre) → lecture seule + bandeau
  //   - run du jour → éditable dès 00:01 (créneau théorique purement informatif)
  // M-2 : `today` figé au mount → après minuit Paris, un refresh est nécessaire
  // pour passer au jour suivant. Limite acceptée (mount = ouverture modale).
  const today = useMemo(() => todayParisIso(), [])

  // Phase 4 PR #141 — fetch infos logement (pieces + accès) via endpoint
  // PR #151 PARTIE F : signature étendue (logement_id, scheduled_date)
  const [info, setInfo] = useState(null)
  const [infoLoading, setInfoLoading] = useState(true)
  const [infoError, setInfoError] = useState(null)
  useEffect(() => {
    let cancelled = false
    setInfoLoading(true); setInfoError(null)
    fetchLogInfo(run.logement_id, run.scheduled_date)
      .then(data => { if (!cancelled) { setInfo(data); setInfoLoading(false) } })
      .catch(e => { if (!cancelled) { setInfoError(e.message || String(e)); setInfoLoading(false) } })
    return () => { cancelled = true }
  }, [run.logement_id, run.scheduled_date])

  // ── ADR-302 — CONSIGNES INTERNES (Chantier 42 Phase 4) ──────────────────
  // Lecture RLS-directe : la base est l'arbitre (un agent ne voit QUE ses
  // consignes). AUCUN cache module-level — une consigne CIBLÉE ne doit jamais
  // survivre à un changement de compte dans le même onglet.
  // INERTIE (leçon R5) : `fetchMyInstructions` ne throw JAMAIS ; migration
  // absente → liste vide → AUCUN rendu. Une consigne inexistante EST une
  // absence de consigne : ne rien afficher est VRAI, et un bandeau d'erreur sur
  // l'app terrain serait du bruit anxiogène.
  const [instructions, setInstructions] = useState([])
  useEffect(() => {
    let cancelled = false
    setInstructions([])
    fetchMyInstructions([run.logement_id]).then(({ instructions: rows }) => {
      if (!cancelled) setInstructions(rows || [])
    })
    return () => { cancelled = true }
  }, [run.logement_id, currentUser?.email])

  // PR #148 BUG 1 — bouton "↻ Recharger" : purge le cache pour ce
  // logement et refetch (cas : admin vient de configurer une checklist
  // dans un autre onglet, ménage veut récupérer sans hard-refresh).
  async function reloadInfo() {
    invalidateLogInfo(run.logement_id)
    setInfoLoading(true); setInfoError(null); setInfo(null)
    try {
      const data = await fetchLogInfo(run.logement_id, run.scheduled_date)
      setInfo(data); setInfoLoading(false)
    } catch (e) {
      setInfoError(e.message || String(e)); setInfoLoading(false)
    }
  }

  // Format date courte "DD/MM" pour le bouton "🔒 Le DD/MM" (Q3 Dimitri)
  const formatDateShort = (yyyymmdd) => {
    if (!yyyymmdd || !/^\d{4}-\d{2}-\d{2}$/.test(yyyymmdd)) return yyyymmdd || '?'
    const [, m, d] = yyyymmdd.split('-')
    return `${d}/${m}`
  }

  // ADR-230 : getEffectiveRunStatus (verrou « heure de check-out ») NE bloque
  // PLUS l'édition ici — l'agent valide dès 00:01 le jour J. La lecture seule
  // dépend désormais de isRunOpenForMenageToday (jour J ∧ verrou 48h).
  // Verrou temporel 48h (ADR-207) : passé scheduled_date + 48h, le rôle MÉNAGE
  // ne peut plus éditer (miroir UX de la RLS menage_update_own). L'ADMIN reste
  // libre (exempté côté RLS par admin_all). Figé au mount comme today/nowHHMM (M-2).
  const pastLocked = useMemo(
    () => currentUser?.role !== 'admin' && isMenageRunLockedForMenage(run.scheduled_date, today),
    [currentUser?.role, run.scheduled_date, today],
  )
  // ADR-230 — lecture seule côté ménage : un run est éditable UNIQUEMENT le jour J
  // (dès 00:01, quelle que soit l'heure), et tant qu'il n'est pas verrouillé 48h.
  // L'ADMIN n'est jamais en lecture seule par ce biais (il garde son cas propre :
  // pastLocked le laisse libre, et il n'est pas borné au jour J).
  const isAdmin = currentUser?.role === 'admin'

  // CHANTIER 42 Phase 3 — un ORCHESTRATEUR n'a aucune policy d'écriture sur un run
  // qu'il n'a PAS en propre (il n'est pas l'assigné). L'UPDATE matche alors 0 ligne
  // (RLS SELECT-only orchestrateur_select_pool) : le message « verrou 48h » serait
  // FAUX. On le distingue pour afficher un message correct « lecture seule — pas
  // l'agent assigné ». N'altère PAS le comportement de l'agent ménage.
  const isOrchestrateur = currentUser?.role === 'orchestrateur'
  const orchestrateurNotOwnRun = isOrchestrateur && run.assigned_provider_email !== currentUser?.email

  // N5 — l'orchestrateur peut désormais OUVRIR la checklist d'un run du pool qu'il
  // n'a pas en propre (via le détail Mois dépliable). L'écriture était déjà
  // RLS/trigger-gardée (0 ligne silencieux) ; on aligne l'UI en ouvrant en lecture
  // seule. Sans ce terme, un run pool-not-own du JOUR s'ouvrirait éditable côté UI
  // (isRunOpenForMenageToday=true), avec des inputs actifs mais des saves à 0 ligne.
  const isReadOnly = (!isAdmin && !isRunOpenForMenageToday(run, today)) || orchestrateurNotOwnRun

  const pieces = (info?.menage?.pieces || []).filter(Boolean)
  const photosByPiece = useMemo(() => groupPhotosByPiece(photos), [photos])

  // ADR-230 — créneau THÉORIQUE du logement (non bloquant, purement informatif).
  // Source : info.timing (horaires config du logement, déjà chargés via
  // fetchLogInfo). JAMAIS le réel voyageur (guest_arrival_time / guest_departure_time).
  // Affiché en haut du body quand le run est ouvert. Si aucun horaire → pas de bandeau.
  const fmtHM = (hm) => (hm ? String(hm).slice(0, 5) : null)
  const theoCheckout = fmtHM(info?.timing?.checkout_time)
  const theoCheckin = fmtHM(info?.timing?.checkin_time)

  // Refresh IDB queue count toutes les 2s tant que la modale est ouverte
  useEffect(() => {
    let cancelled = false
    const tick = async () => {
      try {
        const queue = await getQueueForRun(run.id)
        if (cancelled) return
        const cnt = {}
        queue.forEach(it => { cnt[it.pieceId] = (cnt[it.pieceId] || 0) + 1 })
        setIdbCount(cnt)
      } catch (_) {}
    }
    tick()
    const interval = setInterval(tick, 2000)
    return () => { cancelled = true; clearInterval(interval) }
  }, [run.id])

  // Refresh photos remote au mount + après chaque upload (poll 5s).
  // Chantier photo (Lot 1) : photos de pièce dans un bucket PRIVÉ → on passe par
  // l'endpoint serveur qui renvoie un `url` PRÊT (signed URL pour les `path`
  // privés). Garde le COMPTE intact (toutes les photos commitées renvoyées) →
  // validation photos_min inchangée.
  // CHANTIER 42 Phase 3 (IDOR) : l'ORCHESTRATEUR n'a PAS le droit de lire les
  // photos via /api/admin-users?menage_run_photos (cloisonné logIds JWT seul,
  // SANS le pool). Il passe par l'endpoint dédié pool orchestrateur_run_photos
  // (double cloisonnement pool ∧ logIds, ou self). MÊME shape de réponse
  // ({ photos: [{ piece_id, url, uploaded_at }] }) → parsing identique.
  // (isOrchestrateur déclaré plus haut, près de isReadOnly.)
  useEffect(() => {
    let cancelled = false
    const tick = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) return
      try {
        const res = isOrchestrateur
          ? await fetch('/api/beds24-mapping', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
              body: JSON.stringify({ action: 'orchestrateur_run_photos', run_id: run.id }),
            })
          : await fetch(`/api/admin-users?action=menage_run_photos&run_id=${encodeURIComponent(run.id)}`, {
              headers: { Authorization: `Bearer ${token}` },
            })
        const json = await res.json().catch(() => ({}))
        if (!cancelled && Array.isArray(json.photos)) setPhotos(json.photos)
      } catch (_) { /* swallow — retry au prochain tick */ }
    }
    tick()
    const interval = setInterval(tick, 5000)
    return () => { cancelled = true; clearInterval(interval) }
  }, [run.id, isOrchestrateur])

  // Debounce save items_checked 1s
  // PR #151 PARTIE E : early-return si isReadOnly (run anticipé) → le
  // toggle ne fait rien, et les inputs ont déjà disabled={isReadOnly}.
  function toggleItem(pieceId, itemId) {
    if (isReadOnly || busy) return
    setItemsChecked(prev => {
      // Tolérance double-forme : un legacy booléen devient un tableau ICI.
      const current = cochesEnListe(prev[pieceId], pieces.find((p) => p.id === pieceId))
      const next = current.includes(itemId)
        ? current.filter(x => x !== itemId)
        : [...current, itemId]
      const updated = { ...prev, [pieceId]: next }
      // debounce save
      pendingItems.current = updated
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => { flushItems() }, 1000)
      return updated
    })
  }

  // ⚠️ LOT « TRAVAIL PERDU » — sauvegarde des coches.
  //   · Le statut n'est PLUS réécrit avec les coches. Avant, chaque sauvegarde
  //     envoyait `status: run.status === 'scheduled' ? 'in_progress' : …`,
  //     calculé sur l'état d'OUVERTURE de la fenêtre : une sauvegarde différée
  //     partie après « Terminer » rouvrait le ménage terminé. Le passage en
  //     cours est maintenant une écriture à part, filtrée sur
  //     `status = 'scheduled'` : elle ne peut que faire avancer, jamais reculer.
  //   · Une panne réseau n'est plus avalée : message à l'écran.
  //   · `flushItems()` est aussi appelé par « Terminer » : rien ne reste en
  //     attente au moment de la clôture.
  //   · Relecture : postgrest-js ne LÈVE pas sur une panne réseau, il RENVOIE
  //     `{ error }`. Une erreur renvoyée = panne : coches remises en attente,
  //     message réseau. Le message « verrou 48 h » est réservé au cas
  //     « 0 ligne SANS erreur » (le seul que produit le verrou).
  //   · L'attente se fait EN BOUCLE : tant qu'une sauvegarde est en cours ou
  //     que des coches attendent, « Terminer » ne part pas (une autre
  //     sauvegarde peut démarrer pendant qu'on en attend une).
  // Renvoie true si tout est enregistré (ou s'il n'y avait rien à faire).
  //   · Relecture 2 : l'attente est BORNÉE (_reglagesChecklist.attenteMaxMs,
  //     20 s). Au-delà : « non enregistrée », message réseau, pas de clôture.
  async function flushItems() {
    if (debounceTimer.current) { clearTimeout(debounceTimer.current); debounceTimer.current = null }
    const limite = Date.now() + _reglagesChecklist.attenteMaxMs
    const TROP_LONG = Symbol('trop_long')
    const auPlus = (p) => {
      let t = null
      return Promise.race([
        p,
        new Promise((r) => { t = setTimeout(() => r(TROP_LONG), Math.max(0, limite - Date.now())) }),
      ]).finally(() => clearTimeout(t))
    }
    for (;;) {
      if (!saveInFlight.current) {
        if (!pendingItems.current) return true
        void envoyerCoches() // pose saveInFlight tout de suite ; attendu ci-dessous
        continue
      }
      // Une sauvegarde en cours : l'attendre (au plus jusqu'à la limite), puis
      // tout revérifier — une autre a pu démarrer entre-temps.
      const ok = await auPlus(saveInFlight.current)
      if (ok === TROP_LONG) {
        setError(`Saisie non enregistrée — problème de connexion (pas de réponse après ${Math.round(_reglagesChecklist.attenteMaxMs / 1000)} s). Vérifie le réseau puis réessaie.`)
        return false
      }
      if (!ok) return false
    }
  }

  flushRef.current = flushItems

  async function envoyerCoches() {
    const toSave = pendingItems.current
    if (!toSave) return true
    pendingItems.current = null
    const remettreEnAttente = (detail) => {
      // Remettre la saisie en attente : le prochain essai la renverra (sauf si
      // une coche plus récente attend déjà — c'est elle qui fait foi).
      if (!pendingItems.current) pendingItems.current = toSave
      setError(`Saisie non enregistrée — problème de connexion (${detail}). Vérifie le réseau puis réessaie.`)
      return false
    }
    const p = (async () => {
      try {
        // .select('id') → rowcount : 0 ligne = verrou RLS 48h (UPDATE matche
        // 0 ligne SANS erreur). On NE laisse PAS croire que c'est sauvé.
        const { data, error } = await supabase.from('menage_runs')
          .update({ items_checked: toSave })
          .eq('id', run.id).select('id')
        if (error) return remettreEnAttente(error.message || String(error))
        if (!data || data.length === 0) {
          setError(orchestrateurNotOwnRun
            ? "Lecture seule — vous n'êtes pas l'agent assigné à ce ménage (saisie réservée à l'agent)."
            : "Saisie non enregistrée — ce ménage est fermé (verrou 48 h). Recharge la page.")
          return false
        }
        // Passage « prévu » → « en cours », une seule fois, et SEULEMENT depuis
        // « scheduled » (0 ligne = déjà en cours ou terminé : normal, rien à dire).
        if (run.status === 'scheduled' && !startedRef.current) {
          const { error: stErr } = await supabase.from('menage_runs')
            .update({ status: 'in_progress' })
            .eq('id', run.id).eq('status', 'scheduled').select('id')
          if (stErr) console.error('[checklist] passage en cours', stErr.message || stErr)
          else startedRef.current = true
        }
        return true
      } catch (e) {
        return remettreEnAttente(e?.message || String(e))
      }
    })()
    saveInFlight.current = p
    try { return await p } finally { if (saveInFlight.current === p) saveInFlight.current = null }
  }

  // Validation : pour chaque pièce, photos_min satisfait (locales OU uploadées)
  const blockingPieces = pieces.filter(p => {
    const photosMin = p.photos_min || 0
    const totalPhotos = (photosByPiece[p.id]?.length || 0) + (idbCount[p.id] || 0)
    return totalPhotos < photosMin
  })
  const noChecklistConfigured = !infoLoading && !infoError && pieces.length === 0

  // LOT C (contrat v2 art. 7.2) — le constat photo de fin de ménage FAIT FOI :
  // un AGENT ne peut plus terminer sans AU MOINS 1 photo de pièce. La file
  // locale (IndexedDB, upload retry auto) COMPTE volontairement : si Storage /
  // le réseau est en panne, la photo est bien PRISE (preuve capturée) — la
  // clôture part alors en mode dégradé `photos_pending=true` (le serveur trace
  // + alerte, et la notif admin « upload failed » existe déjà après 3 retries).
  // On ne bloque JAMAIS le terrain pour une panne technique (choix documenté,
  // garde serveur alignée dans lib/menage-run-complete.js). ADMIN exempté.
  // Les entrées incident legacy (piece_id 'incident:*') ne comptent pas.
  const totalIdbPending = Object.values(idbCount).reduce((a, b) => a + b, 0)
  const piecePhotoCount = photos.filter(
    p => p && !String(p.piece_id || '').startsWith('incident')
  ).length
  const missingProofPhoto = !isAdmin && (piecePhotoCount + totalIdbPending) === 0
  const canComplete = !infoLoading && !infoError
    && (noChecklistConfigured || blockingPieces.length === 0)
    && !missingProofPhoto

  const piecesComplete = pieces.length - blockingPieces.length
  const piecesTotal = pieces.length

  // ⚠️ LE GESTE LE PLUS FAIT DE L'APPLICATION, et sur téléphone à chaque fois
  // (Dimitri 2026-08-22) : il ne passe plus par une boîte du navigateur mais
  // par notre grammaire — feuille qui monte sur téléphone, carte centrée sur
  // ordinateur. La phrase DIT ce qui va se passer, pas « êtes-vous sûr ».
  function complete() {
    if (!canComplete || busy) return
    setConfirmFin(true)
  }

  async function completeConfirme() {
    setConfirmFin(false)
    setBusy(true); setError(null)
    try {
      // Vider la sauvegarde des coches en attente AVANT la clôture : sinon elle
      // partirait après « terminé ». Si elle échoue, on ne clôt pas — le
      // message d'erreur est déjà affiché, l'agent réessaie.
      const coches = await flushItems()
      if (!coches) return
      const photosPending = Object.values(idbCount).reduce((a, b) => a + b, 0) > 0

      // LOT C — la clôture AGENT (menage / orchestrateur sur son run) passe par
      // l'action serveur `menage_run_complete` : garde photo fail-closed côté
      // serveur (contrat v2 art. 7.2) + écriture user-scoped (la RLS verrou 48h
      // ADR-207 reste l'arbitre — le patch ADR-208 auteur/correcteur est
      // construit serveur, à l'identique). L'ADMIN garde le chemin client
      // direct historique ci-dessous (exemption : correction a posteriori).
      if (!isAdmin) {
        const { data: { session } } = await supabase.auth.getSession()
        const token = session?.access_token
        if (!token) {
          setError('Session expirée — recharge la page et reconnecte-toi.')
          return
        }
        const res = await fetch('/api/beds24-mapping', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ action: 'menage_run_complete', run_id: run.id, photos_pending: photosPending }),
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) {
          if (json?.error === 'photo_required') {
            setError(json.message || 'Impossible de terminer sans photo : ajoute au moins une photo de pièce (le constat photo fait foi).')
          } else if (res.status === 409) {
            setError(orchestrateurNotOwnRun
              ? "Lecture seule — vous n'êtes pas l'agent assigné à ce ménage. La validation est réservée à l'agent."
              : "Ce ménage est fermé (verrou 48 h) — impossible de le valider. Contacte l'admin si une correction est nécessaire.")
          } else {
            setError(json?.message || `La validation n'a pas abouti (erreur ${res.status}). Réessaie ou contacte l'admin.`)
          }
          return
        }
        onCompleted()
        return
      }

      // ADR-208 — trace auteur vs correcteur :
      //   - 1re clôture (run jamais terminé : ménage OU admin qui rattrape un
      //     non-saisi = c'est lui qui l'a fait) → on pose completed_at +
      //     completed_by_email (l'auteur du ménage).
      //   - correction (run DÉJÀ completed) → on PRÉSERVE completed_at +
      //     completed_by_email d'origine et on trace le correcteur séparément
      //     (corrected_by/corrected_at). Jamais d'écrasement de l'auteur.
      const now = new Date().toISOString()
      const alreadyDone = !!run.completed_at
      const patchObj = { status: 'completed', photos_pending: photosPending }
      if (alreadyDone) {
        patchObj.corrected_by = currentUser.email
        patchObj.corrected_at = now
      } else {
        patchObj.completed_at = now
        patchObj.completed_by_email = currentUser.email
      }
      // .select('id') → rowcount. Un UPDATE bloqué par le verrou RLS 48h matche
      // 0 ligne SANS erreur ({data:[], error:null}). Sans ce garde, onCompleted()
      // serait appelé → fausse confirmation « Terminé » alors que rien n'est
      // persisté (run reste scheduled). CRITIQUE : pas de faux succès.
      const { data, error } = await supabase.from('menage_runs').update(patchObj).eq('id', run.id).select('id')
      if (error) throw error
      if (!data || data.length === 0) {
        setError(orchestrateurNotOwnRun
          ? "Lecture seule — vous n'êtes pas l'agent assigné à ce ménage. La validation est réservée à l'agent."
          : "Ce ménage est fermé (verrou 48 h) — impossible de le valider. Contacte l'admin si une correction est nécessaire.")
        return
      }
      onCompleted()
    } catch (e) { setError(e.message || String(e)) }
    finally { setBusy(false) }
  }

  return (
    <>
    {/* LA FIN DU MÉNAGE SE CONFIRME DANS NOTRE GRAMMAIRE (2026-08-22). */}
    {confirmFin && (
      <FeuilleConfirmation
        testId="checklist-confirmer-fin"
        titre="Terminer ce ménage ?"
        texte={`Le ménage sera marqué comme fait${piecesTotal > 0 ? ` avec ${piecesComplete} pièce${piecesComplete > 1 ? 's' : ''} sur ${piecesTotal}` : ''}. Vos photos partent avec, et FBZ pourra contrôler le logement. Vous ne pourrez plus modifier la checklist.`}
        libelleConfirmer="Terminer le ménage"
        libelleRenoncer="Continuer le ménage"
        ton="action"
        occupe={busy}
        onConfirmer={completeConfirme}
        onRenoncer={() => setConfirmFin(false)} />
    )}
    <div onClick={busy ? undefined : onClose}
      style={{position:'fixed',inset:0,background:'rgba(0,0,0,.55)',zIndex:1100,display:'flex',alignItems:'flex-start',justifyContent:'center',padding:0,fontFamily:DS.fontBody}}>
      <div onClick={e => e.stopPropagation()}
        className="fbz-modal-sheet"
        style={{background:DS.card,width:'100%',maxWidth:720,height:'100vh',maxHeight:'100vh',display:'flex',flexDirection:'column',color:DS.navyDark}}>
        {/* Header sticky */}
        <div style={{padding:'16px 18px',borderBottom:`1px solid ${DS.border}`,display:'flex',justifyContent:'space-between',alignItems:'center',position:'sticky',top:0,background:DS.card,zIndex:1}}>
          <div>
            <div style={{fontSize:11,letterSpacing:'.18em',textTransform:'uppercase',color:DS.textMuted,fontWeight:600}}>Checklist ménage</div>
            <div style={{fontFamily:DS.fontDisplay,fontSize:18,fontWeight:600,color:DS.marine}}>{info?.nom || run.logement_id}</div>
          </div>
          <button onClick={onClose} disabled={busy} aria-label="Fermer" className="fbz-tap"
            style={{background:'transparent',border:'none',fontSize:26,cursor:busy?'not-allowed':'pointer',color:DS.textMuted,padding:4,lineHeight:1,display:'inline-flex',alignItems:'center',justifyContent:'center'}}>×</button>
        </div>

        {/* Body */}
        <div style={{flex:1,overflowY:'auto',padding:'14px 16px 8px'}}>
          {/* Phase 4 PR #141 — section Accès en haut (utile pendant le ménage) */}
          <AccessSection info={info} loading={infoLoading} error={infoError} />
          <TimingSection timing={info?.timing} />
          {/* À APPORTER — rappel EN TÊTE, avant les pièces (même donnée que la carte). */}
          <BlocAApporter produits={info?.a_apporter} testId="a-apporter-checklist" />

          {/* ADR-302 — CONSIGNES INTERNES, AU-DESSUS des pièces (l'agent les lit
              AVANT d'intervenir). Aucun rendu si la liste est vide : ni cadre,
              ni « aucune consigne » — le silence est la bonne réponse. */}
          <InstructionsSection instructions={instructions} />

          {/* ADR-207 — verrou 48h (passé) : saisie fermée. */}
          {pastLocked ? (
            <div style={{display:'flex',gap:9,alignItems:'flex-start',padding:'14px 16px',background:DS.cielSoft,color:DS.marineDeep,borderRadius:8,fontSize:13,marginBottom:14,marginTop:10,lineHeight:1.5,border:`1px solid ${DS.ciel}55`}}>
              <Lock size={16} strokeWidth={2.2} style={{flexShrink:0,marginTop:2}}/><span>Ce ménage du <strong>{formatDateFR(run.scheduled_date)}</strong> est <strong>fermé</strong> (verrou 48&nbsp;h) — la saisie n'est plus possible. Contacte l'admin si une correction est nécessaire.</span>
            </div>
          ) : orchestrateurNotOwnRun ? (
            /* N5 — orchestrateur consultant un run du POOL qu'il n'a pas en propre :
               lecture seule (l'écriture est de toute façon RLS/trigger-gardée à 0
               ligne). Message correct : ce n'est PAS une histoire de jour J, mais
               d'agent assigné. Réassignation possible depuis la carte du détail. */
            <div style={{display:'flex',gap:9,alignItems:'flex-start',padding:'14px 16px',background:DS.warningBg,color:DS.warningText,borderRadius:8,fontSize:13,marginBottom:14,marginTop:10,lineHeight:1.5,border:`1px solid ${DS.warning}55`}}>
              <Lock size={16} strokeWidth={2.2} style={{flexShrink:0,marginTop:2}}/><span>Lecture seule — vous n'êtes pas l'agent assigné à ce ménage. La saisie est réservée à l'agent (vous pouvez le réassigner depuis la liste).</span>
            </div>
          ) : isReadOnly ? (
            /* ADR-230 — run pas du jour (futur, ou veille hors fenêtre) : la
               checklist reste consultable mais la saisie est réservée au jour J.
               (Égalité stricte côté helper : pas de pré-saisie anticipée.) */
            <div style={{display:'flex',gap:9,alignItems:'flex-start',padding:'14px 16px',background:DS.warningBg,color:DS.warningText,borderRadius:8,fontSize:13,marginBottom:14,marginTop:10,lineHeight:1.5,border:`1px solid ${DS.warning}55`}}>
              <Calendar size={16} strokeWidth={2.2} style={{flexShrink:0,marginTop:2}}/><span>Ce ménage est prévu le <strong>{formatDateFR(run.scheduled_date)}</strong>. Tu pourras le valider le jour même (dès le matin).</span>
            </div>
          ) : (theoCheckout || theoCheckin) && (
            /* ADR-230 — créneau THÉORIQUE du logement (NON bloquant, informatif).
               Décision Dimitri : on n'empêche plus la validation avant l'heure ;
               on indique seulement le créneau de référence du logement. */
            <div style={{display:'flex',gap:9,alignItems:'flex-start',padding:'12px 16px',background:DS.cielSoft,color:DS.marineDeep,borderRadius:8,fontSize:13,marginBottom:14,marginTop:10,lineHeight:1.5,border:`1px solid ${DS.ciel}55`}}>
              <Clock size={16} strokeWidth={2.2} style={{flexShrink:0,marginTop:2}}/><span>Créneau du logement : {theoCheckout ? <>check-out <strong>{theoCheckout}</strong></> : <>check-out —</>}
              {theoCheckin && <> → check-in <strong>{theoCheckin}</strong></>}{' '}
              <span style={{color:DS.textSecondary}}>(horaire indicatif — tu peux valider dès que le ménage est fait)</span></span>
            </div>
          )}

          {infoLoading ? (
            <div style={{display:'flex',gap:8,justifyContent:'center',alignItems:'center',padding:'24px 16px',fontSize:13,color:DS.textMuted}}><Loader2 size={15}/> Chargement de la checklist…</div>
          ) : infoError ? (
            <div style={{padding:'14px 16px',background:DS.dangerBg,color:DS.dangerText,borderRadius:8,fontSize:13,marginBottom:14,lineHeight:1.5}}>
              <span style={{display:'inline-flex',alignItems:'center',gap:7}}><AlertCircle size={15} strokeWidth={2.2}/> Erreur chargement checklist : {infoError}</span>
              <div style={{marginTop:10}}>
                <button type="button" className="fbz-press" onClick={reloadInfo}
                  style={{display:'inline-flex',alignItems:'center',gap:5,background:DS.card,border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,padding:'6px 12px',fontSize:12,cursor:'pointer',color:DS.marine,fontFamily:DS.fontBody,fontWeight:600}}>
                  <RefreshCw size={13} strokeWidth={2.2}/> Recharger
                </button>
              </div>
            </div>
          ) : noChecklistConfigured ? (
            <>
              <div style={{padding:'14px 16px',background:DS.warningBg,borderRadius:8,fontSize:13,color:DS.warningText,lineHeight:1.5,marginBottom:14}}>
                {/* ⚠️ CE QUE L'AGENT LIT ICI A ÉTÉ DÉPOUILLÉ (Dimitri 2026-08-12).
                    L'ancien texte lui apprenait qu'une CONFIGURATION manquait,
                    QUI devait la faire et OÙ — trois informations dont il n'a
                    que faire et sur lesquelles il ne peut rien. Il portait en
                    plus « fait foi de l'état du logement », du vocabulaire de
                    preuve qui n'a rien à faire devant quelqu'un qui vient
                    nettoyer. Reste la seule chose actionnable : la consigne.
                    ⚠️ L'ALERTE, ELLE, DOIT REMONTER À L'ORCHESTRATEUR — elle ne
                    doit pas mourir dans cet écran. Emplacement à trancher avec
                    Dimitri avant mise en production (proposition du 2026-08-12). */}
                <span style={{display:'inline-flex',alignItems:'flex-start',gap:7}}><Info size={15} strokeWidth={2.2} style={{flexShrink:0,marginTop:2}}/><span>{isAdmin
                  ? <>Aucune checklist configurée pour ce logement — le ménage peut être marqué terminé directement.</>
                  : <>Prenez une photo de chaque pièce avant de terminer.</>}</span></span>
                <div style={{marginTop:10}}>
                  <button type="button" className="fbz-press" onClick={reloadInfo}
                    style={{display:'inline-flex',alignItems:'center',gap:5,background:DS.card,border:`0.5px solid ${DS.borderHover}`,borderRadius:DS.radiusControl,padding:'6px 12px',fontSize:12,cursor:'pointer',color:DS.marine,fontFamily:DS.fontBody,fontWeight:600}}>
                    <RefreshCw size={13} strokeWidth={2.2}/> Recharger
                  </button>
                </div>
              </div>
              {/* LOT C — bloc photo GÉNÉRIQUE : sans checklist, l'agent n'aurait
                  AUCUN bouton photo alors que la photo de fin de ménage est
                  requise pour terminer (contrat v2 art. 7.2) → on lui donne un
                  bloc de capture (piece_id 'general', même file d'upload).
                  Jamais rendu pour l'admin (exempté, comportement inchangé). */}
              {!isAdmin && (
                <PieceBlock
                  piece={GENERIC_PHOTO_PIECE}
                  run={run}
                  photos={photosByPiece[GENERIC_PHOTO_PIECE.id] || []}
                  idbPending={idbCount[GENERIC_PHOTO_PIECE.id] || 0}
                  checkedItems={[]}
                  onToggleItem={() => {}}
                  onPhotoQueued={() => {
                    setErreurPhoto(null)
                    setIdbCount(prev => ({ ...prev, [GENERIC_PHOTO_PIECE.id]: (prev[GENERIC_PHOTO_PIECE.id] || 0) + 1 }))
                  }}
                  onPhotoError={setErreurPhoto}
                  logId={run.logement_id}
                  readOnly={isReadOnly}
                />
              )}
            </>
          ) : pieces.map(p => (
            <PieceBlock
              key={p.id}
              piece={p}
              run={run}
              photos={photosByPiece[p.id] || []}
              idbPending={idbCount[p.id] || 0}
              checkedItems={cochesEnListe(itemsChecked[p.id], p)}
              onToggleItem={(itemId) => toggleItem(p.id, itemId)}
              onPhotoQueued={() => {
                setErreurPhoto(null)
                setIdbCount(prev => ({ ...prev, [p.id]: (prev[p.id] || 0) + 1 }))
              }}
              onPhotoError={setErreurPhoto}
              logId={run.logement_id}
              readOnly={isReadOnly}
              cochesBloquees={busy}
            />
          ))}

          {/* SIGNALER UN INCIDENT — le moment « je le découvre EN NETTOYANT »
              (Dimitri 2026-08-21 : l'incident vit aux DEUX endroits — la carte
              avant de commencer, ICI pendant le travail). MÊME formulaire,
              MÊMES règles (2 photos min, même fenêtre) : le parent ouvre le
              même IncidentModal que la carte. */}
          {onIncident && !isReadOnly && !pastLocked && (
            <button type="button" data-testid="checklist-incident"
              onClick={() => onIncident(run)} className="fbz-press fbz-tap"
              style={{ marginTop: 14, width: '100%', minHeight: 44, display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center', gap: 7, background: '#fff',
                color: '#8f2f1d', border: '1px solid #b8412b', borderRadius: 9, padding: 11,
                fontSize: 12.5, fontWeight: 700, fontFamily: DS.fontBody, cursor: 'pointer' }}>
              <AlertCircle size={15} strokeWidth={2} /> Signaler un incident
            </button>
          )}

          {/* SIGNALER UN MANQUE (2026-08-21) — l'agent signale, il ne dépose
              rien ; pas de verrou 48 h (on n'écrit pas le run). */}
          {currentUser?.role === 'menage' && !!cfg && (run.scheduled_date || '') <= today && (
            <SectionSignalerManque cfg={cfg} logementId={run.logement_id} />
          )}

          {/* L'ERREUR DE PHOTO — informative, dans l'écran, avec la marche à
              suivre (elle ne demande rien : ce n'est pas une confirmation). */}
          {erreurPhoto && (
            <div data-testid="checklist-erreur-photo"
              style={{display:'flex',gap:7,alignItems:'flex-start',padding:'10px 12px',background:DS.dangerBg,color:DS.dangerText,borderRadius:8,fontSize:12,marginBottom:12}}>
              <AlertCircle size={14} strokeWidth={2.2} style={{flexShrink:0,marginTop:1}}/>
              <span style={{flex:1}}>{erreurPhoto}</span>
              <button type="button" data-testid="checklist-erreur-photo-fermer" onClick={() => setErreurPhoto(null)}
                aria-label="Masquer ce message"
                style={{border:'none',background:'none',color:'inherit',cursor:'pointer',fontWeight:700,padding:0}}>✕</button>
            </div>
          )}
          {error && <div style={{display:'flex',gap:7,alignItems:'flex-start',padding:'10px 12px',background:DS.dangerBg,color:DS.dangerText,borderRadius:8,fontSize:12,marginBottom:12}}><AlertCircle size={14} strokeWidth={2.2} style={{flexShrink:0,marginTop:1}}/><span>{error}</span></div>}
        </div>

        {/* Footer sticky */}
        <div style={{padding:'14px 18px',borderTop:`1px solid ${DS.border}`,position:'sticky',bottom:0,background:DS.card}}>
          {!infoLoading && !infoError && !noChecklistConfigured && (
            <div style={{fontSize:12,color:DS.textSecondary,marginBottom:10}}>
              <strong>{piecesComplete}/{piecesTotal}</strong> pièce{piecesTotal > 1 ? 's' : ''} complète{piecesComplete > 1 ? 's' : ''}
              {blockingPieces.length > 0 && (
                <> — <span style={{color:DS.warningText}}>
                  {blockingPieces.map(p => `${p.name} ${(photosByPiece[p.id]?.length || 0) + (idbCount[p.id] || 0)}/${p.photos_min}`).join(', ')}
                </span> photos manquantes</>
              )}
            </div>
          )}
          {/* LOT C — message explicite quand la photo obligatoire manque (agent
              seulement ; jamais affiché sur un run en lecture seule). */}
          {missingProofPhoto && !isReadOnly && !infoLoading && !infoError && (
            <div style={{display:'flex',gap:8,alignItems:'flex-start',padding:'10px 12px',background:DS.warningBg,color:DS.warningText,borderRadius:8,fontSize:12,marginBottom:10,lineHeight:1.5}}>
              <Camera size={15} strokeWidth={2.2} style={{flexShrink:0,marginTop:1}}/>
              <span>Ajoute au moins <strong>1 photo de fin de ménage</strong> pour pouvoir terminer : les photos horodatées font foi de l'état du logement après chaque départ (contrat de location, art.&nbsp;7.2).</span>
            </div>
          )}
          <button className="fbz-press" onClick={complete} disabled={isReadOnly || !canComplete || busy}
            title={
              pastLocked
                ? `Fermé : ce ménage du ${formatDateShort(run.scheduled_date)} n'est plus modifiable (verrou 48 h)`
                : orchestrateurNotOwnRun
                  ? "Lecture seule — vous n'êtes pas l'agent assigné à ce ménage"
                  : isReadOnly
                    ? `Disponible le jour du ménage (${formatDateShort(run.scheduled_date)})`
                    : missingProofPhoto
                      ? 'Au moins 1 photo de fin de ménage est requise (le constat photo fait foi — contrat art. 7.2)'
                      : (!canComplete ? `Photos manquantes : ${blockingPieces.map(p => p.name).join(', ')}` : '')
            }
            style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,width:'100%',padding:'14px 16px',background: (isReadOnly || !canComplete || busy) ? DS.borderHover : DS.corail,color:'#fff',border:'none',borderRadius:DS.radiusControl,fontSize:14,fontWeight:700,cursor:(isReadOnly || !canComplete || busy)?'not-allowed':'pointer',fontFamily:DS.fontBody}}>
            {pastLocked
              ? <><Lock size={16} strokeWidth={2.3}/>Fermé (48 h)</>
              : orchestrateurNotOwnRun
                ? <><Lock size={16} strokeWidth={2.3}/>Lecture seule</>
                : isReadOnly
                  ? <><Lock size={16} strokeWidth={2.3}/>Disponible {formatDateShort(run.scheduled_date)}</>
                  : (busy ? <><Loader2 size={16}/>Validation…</> : <><Check size={17} strokeWidth={2.4}/>Terminer le ménage</>)}
          </button>
        </div>
      </div>
    </div>
    </>
  )
}

// ── InstructionsSection (ADR-302) ───────────────────────────────────
// Bloc « Consignes internes » rendu AU-DESSUS des pièces. Présentation PURE :
// la liste est déjà cloisonnée par la RLS et triée par l'API client
// (« importante » d'abord, puis les plus récentes).
//
// INERTIE : liste vide (y compris « migration non appliquée ») → `null`. Pas de
// cadre vide, pas de « aucune consigne », pas de bandeau d'erreur.
// R9 (anti-bruit) : au-delà de 5 consignes actives, les suivantes sont repliées
// derrière un « + N autres » — la checklist ne doit jamais être noyée.
export function InstructionsSection({ instructions }) {
  const [showAll, setShowAll] = useState(false)
  const rows = instructions || []
  if (rows.length === 0) return null
  const VISIBLE = 5
  const shown = showAll ? rows : rows.slice(0, VISIBLE)
  const hidden = rows.length - shown.length

  return (
    <div data-testid="consignes-internes" style={{marginTop:10,marginBottom:14}}>
      <div style={{fontSize:11,letterSpacing:'.14em',textTransform:'uppercase',color:DS.textMuted,fontWeight:700,marginBottom:7}}>
        Consignes internes
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:7}}>
        {shown.map(ins => {
          const important = ins.priority === 'importante'
          return (
            <div key={ins.id}
              style={{
                display:'flex',gap:9,alignItems:'flex-start',
                padding:'11px 13px',borderRadius:8,lineHeight:1.5,fontSize:13,
                background: important ? DS.corailSoft : DS.cielSoft,
                color: important ? DS.corailText : DS.marineDeep,
                border:`1px solid ${important ? `${DS.corail}44` : `${DS.ciel}55`}`,
              }}>
              {important
                ? <AlertTriangle size={16} strokeWidth={2.2} style={{flexShrink:0,marginTop:2}} />
                : <Info size={16} strokeWidth={2.2} style={{flexShrink:0,marginTop:2}} />}
              <span style={{minWidth:0}}>
                {ins.title && <strong style={{display:'block',marginBottom:2}}>{ins.title}</strong>}
                <span style={{whiteSpace:'pre-wrap'}}>{ins.body}</span>
              </span>
            </div>
          )
        })}
      </div>
      {hidden > 0 && (
        <button type="button" onClick={() => setShowAll(true)}
          style={{marginTop:7,background:'transparent',border:'none',color:DS.marine,fontSize:12,fontWeight:700,cursor:'pointer',padding:'4px 0',fontFamily:DS.fontBody}}>
          + {hidden} autre{hidden > 1 ? 's' : ''}
        </button>
      )}
    </div>
  )
}

// ── PieceBlock ──────────────────────────────────────────────────────

// `cochesBloquees` : cases figées pendant l'envoi de la clôture (lot « travail
// perdu ») — une coche posée à ce moment partirait après « terminé ».
function PieceBlock({ piece, run, photos, idbPending, checkedItems, onToggleItem, onPhotoQueued, onPhotoError = () => {}, logId, readOnly = false, cochesBloquees = false }) {
  const cameraRef = useRef(null)
  const galleryRef = useRef(null)
  const totalPhotos = photos.length + idbPending
  const photosOK = totalPhotos >= (piece.photos_min || 0)
  const items = (piece.items || []).filter(Boolean)

  async function handleFile(e) {
    const file = e.target.files?.[0]
    if (!file) return
    e.target.value = ''
    try {
      await enqueuePhoto({
        runId: run.id,
        logementId: logId,
        pieceId: piece.id,
        file,
        kind: 'piece',
      })
      onPhotoQueued()
    } catch (err) {
      // ⚠️ UNE ERREUR N'EST PAS UNE CONFIRMATION (Dimitri 2026-08-22) : elle
      // ne demande rien, elle informe — dans l'écran, en disant QUOI FAIRE.
      onPhotoError(`La photo n'a pas pu être ajoutée (${err.message}). Réessayez ; si ça persiste, terminez le ménage sans elle et prévenez FBZ.`)
    }
  }

  return (
    <div style={{border:`1.5px solid ${photosOK ? DS.success+'66' : DS.border}`,borderRadius:DS.radiusCard,padding:14,marginBottom:12,background: photosOK ? DS.successBg : DS.card, opacity: readOnly ? 0.75 : 1}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
        <div style={{fontWeight:700,fontSize:15,color:DS.marine}}>{piece.icon} {piece.name}</div>
        <div style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:11,color: photosOK ? DS.successText : DS.warningText,fontWeight:700}}>
          <Camera size={13} strokeWidth={2.2}/> {totalPhotos}/{piece.photos_min || 0} {photosOK ? <Check size={13} strokeWidth={2.6}/> : 'min'}
        </div>
      </div>

      {/* Items */}
      {items.length > 0 && (
        <div style={{marginBottom:12}}>
          {items.map(it => (
            <label key={it.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',fontSize:14,cursor: readOnly ? 'not-allowed' : 'pointer'}}>
              <input type="checkbox" checked={checkedItems.includes(it.id)}
                onChange={() => onToggleItem(it.id)}
                disabled={readOnly || cochesBloquees}
                style={{accentColor:DS.navy,width:20,height:20,cursor: readOnly ? 'not-allowed' : 'pointer'}}/>
              <span style={{color: checkedItems.includes(it.id) ? DS.textMuted : DS.navyDark, textDecoration: checkedItems.includes(it.id) ? 'line-through' : 'none'}}>
                {it.label}
              </span>
            </label>
          ))}
        </div>
      )}

      {/* Photos preview */}
      {photos.length > 0 && (
        <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:8}}>
          {photos.map((p, i) => p.url ? (
            <a key={i} href={p.url} target="_blank" rel="noopener noreferrer"
              style={{position:'relative',display:'block',width:64,height:64,borderRadius:6,overflow:'hidden',border:`1px solid ${DS.border}`}}>
              <img src={p.url} alt={`preuve ${i+1}`} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
              <span style={{position:'absolute',top:2,right:2,background:DS.success,color:'#fff',padding:'2px',borderRadius:3,display:'inline-flex'}}><Check size={10} strokeWidth={3}/></span>
            </a>
          ) : null)}
          {idbPending > 0 && Array.from({ length: idbPending }).map((_, i) => (
            <div key={`p-${i}`} style={{width:64,height:64,borderRadius:6,background:DS.warningBg,border:`1px dashed ${DS.warning}`,display:'flex',alignItems:'center',justifyContent:'center',color:DS.warningText}} title="En attente upload (retry auto)">
              <Loader2 size={20}/>
            </div>
          ))}
        </div>
      )}

      {/* Caméra DIRECTE (capture) + Galerie en secours (sans capture). */}
      <div style={{display:'flex',gap:8}}>
        <button type="button" className="fbz-press" onClick={() => cameraRef.current?.click()} disabled={readOnly}
          style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:7,flex:2,padding:'12px 14px',border:`1.5px dashed ${DS.ciel}`,background:'transparent',color: readOnly ? DS.textMuted : DS.marine,borderRadius:DS.radiusControl,fontSize:13,fontWeight:600,cursor: readOnly ? 'not-allowed' : 'pointer',fontFamily:DS.fontBody, opacity: readOnly ? 0.55 : 1}}>
          <Camera size={16} strokeWidth={2.2}/> Prendre une photo
        </button>
        <button type="button" className="fbz-press" onClick={() => galleryRef.current?.click()} disabled={readOnly}
          style={{display:'inline-flex',alignItems:'center',justifyContent:'center',gap:6,flex:1,padding:'12px 10px',border:`1.5px dashed ${DS.ciel}`,background:'transparent',color: readOnly ? DS.textMuted : DS.marine,borderRadius:DS.radiusControl,fontSize:13,fontWeight:600,cursor: readOnly ? 'not-allowed' : 'pointer',fontFamily:DS.fontBody, opacity: readOnly ? 0.55 : 1}}>
          <ImageIcon size={16} strokeWidth={2.2}/> Galerie
        </button>
      </div>
      {/* capture="environment" → appareil arrière direct. Sans capture → galerie/fichiers. */}
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={handleFile} disabled={readOnly} style={{display:'none'}}/>
      <input ref={galleryRef} type="file" accept="image/*" onChange={handleFile} disabled={readOnly} style={{display:'none'}}/>
    </div>
  )
}
